/**
 * AvailableSlotList Component
 * Displays available slots for users to book
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { AccessTime, CheckCircle } from '@mui/icons-material';
import { openBookingDialog } from '../../store/slices/bookingSlice';
import { groupSlotsByPeriod, formatSlotTime, getPeriodLabel } from '../../utils/slotUtils';

const AvailableSlotList = ({ slots, loading }) => {
  const dispatch = useDispatch();
  const { selectedSlot } = useSelector(state => state.booking);

  const groupedSlots = groupSlotsByPeriod(slots || []);

  const handleSlotSelect = (slot) => {
    console.log('[AvailableSlotList] Slot selected:', slot);
    dispatch(openBookingDialog(slot));
  };

  const isSlotSelected = (slotId) => {
    return selectedSlot?.ID === slotId;
  };

  const renderSlotCard = (slot) => {
    const selected = isSlotSelected(slot.ID);
    const timeDisplay = formatSlotTime(slot.start_time, slot.end_time);

    return (
      <Grid item xs={12} sm={6} md={4} key={slot.ID}>
        <Card
          sx={{
            border: selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3,
            },
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime fontSize="small" color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="div">
                {timeDisplay}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {getPeriodLabel(slot.period)}
            </Typography>
            {slot.is_available && (
              <Chip
                label="Available"
                size="small"
                color="success"
                icon={<CheckCircle />}
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
          <CardActions>
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => handleSlotSelect(slot)}
              disabled={!slot.is_available}
            >
              Book This Slot
            </Button>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const renderPeriodSlots = (period, periodSlots) => {
    if (periodSlots.length === 0) return null;

    return (
      <Box key={period} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {getPeriodLabel(period)}
          </Typography>
          <Chip
            label={`${periodSlots.length} slot${periodSlots.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>
        <Grid container spacing={2}>
          {periodSlots.map(slot => renderSlotCard(slot))}
        </Grid>
        <Divider sx={{ mt: 3 }} />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading available slots...
        </Typography>
      </Box>
    );
  }

  if (!slots || slots.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No available slots for this date
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please select another date or check back later
        </Typography>
      </Paper>
    );
  }

  const totalSlots = slots.length;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Available Time Slots
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {totalSlots} slot{totalSlots !== 1 ? 's' : ''} available • Select a slot to book
        </Typography>
      </Box>

      {renderPeriodSlots('morning', groupedSlots.morning)}
      {renderPeriodSlots('noon', groupedSlots.noon)}
      {renderPeriodSlots('evening', groupedSlots.evening)}
    </Box>
  );
};

export default AvailableSlotList;
